import { useState, useEffect } from 'react';
import { Award, ClipboardCheck, GitCompare, FileText, Folder, Briefcase, Play, Send, Loader, CheckCircle, Target, Upload } from 'lucide-react';
import { getSelfAssessmentForm, getProfileComparison, generateVaeDossier, getPortfolio, getCareerSuggestions, simulateEligibility, getInterviewGuide, getCounselorPartners } from '../services/vae';

export default function Echeveau() {
  const [activeTab, setActiveTab] = useState('assessment');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const tabs = [
    { id: 'assessment', label: 'Auto-évaluation', icon: ClipboardCheck },
    { id: 'comparison', label: 'Comparaison Profil', icon: GitCompare },
    { id: 'dossier', label: 'Dossier VAE', icon: FileText },
    { id: 'portfolio', label: 'Portfolio', icon: Folder },
    { id: 'suggestions', label: 'Suggestions Métiers', icon: Briefcase },
    { id: 'eligibility', label: 'Simulation', icon: Play },
    { id: 'guide', label: 'Guide Entretien', icon: Target },
    { id: 'export', label: 'Export Conseillers', icon: Send }
  ];

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      let result;
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      switch (activeTab) {
        case 'assessment': result = await getSelfAssessmentForm(); break;
        case 'comparison': result = await getProfileComparison(user.id || 1); break;
        case 'dossier': result = { dossier: { sections: [{ title: 'Parcours', progress: 80 }, { title: 'Compétences', progress: 60 }], overallProgress: 70 } }; break;
        case 'portfolio': result = await getPortfolio(user.id || 1); break;
        case 'suggestions': result = await getCareerSuggestions(user.id || 1); break;
        case 'eligibility': result = await simulateEligibility(1, 5); break;
        case 'guide': result = await getInterviewGuide(1); break;
        case 'export': result = await getCounselorPartners(); break;
      }
      setData(result);
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, [activeTab]);

  const renderContent = () => {
    if (loading) return <div className="flex items-center justify-center py-12"><Loader className="w-8 h-8 animate-spin text-teal-600" /></div>;
    if (error) return <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">{error}</div>;

    switch (activeTab) {
      case 'assessment':
        return (
          <div className="space-y-4">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Formulaire d'auto-évaluation</h2>
            {data?.form?.competences?.map((comp, i) => (
              <div key={i} className="border border-gray-200 rounded-lg p-4">
                <div className="font-medium text-gray-900 mb-2">{comp.label}</div>
                <div className="flex gap-2">
                  {comp.levels.map((level, j) => (
                    <button key={j} className="px-3 py-1 border border-gray-300 rounded-lg hover:bg-teal-50 text-sm">{level}</button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        );

      case 'comparison':
        return (
          <div className="space-y-6">
            <div className="bg-gradient-to-r from-teal-50 to-cyan-50 rounded-lg p-6 border border-teal-200">
              <div className="text-center">
                <div className="text-4xl font-bold text-teal-600 mb-2">{data?.comparison?.matchScore}%</div>
                <div className="text-sm text-gray-600">Score de correspondance</div>
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="border border-green-200 rounded-lg p-4 bg-green-50">
                <h3 className="font-semibold text-green-900 mb-3">Points forts</h3>
                {data?.comparison?.strengths?.map((s, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-green-800 mb-1">
                    <CheckCircle className="w-4 h-4" />
                    {s}
                  </div>
                ))}
              </div>
              <div className="border border-orange-200 rounded-lg p-4 bg-orange-50">
                <h3 className="font-semibold text-orange-900 mb-3">Écarts à combler</h3>
                {data?.comparison?.gaps?.map((g, i) => (
                  <div key={i} className="text-sm text-orange-800 mb-1">• {g}</div>
                ))}
              </div>
            </div>
          </div>
        );

      case 'dossier':
        return (
          <div className="space-y-4">
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6">
              <h3 className="font-bold text-blue-900 mb-2">Progression du dossier</h3>
              <div className="text-3xl font-bold text-blue-600">{data?.dossier?.overallProgress}%</div>
            </div>
            {data?.dossier?.sections?.map((section, i) => (
              <div key={i} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium">{section.title}</span>
                  <span className="text-sm font-semibold text-indigo-600">{section.progress}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div className="h-full bg-indigo-500 rounded-full" style={{ width: `${section.progress}%` }} />
                </div>
              </div>
            ))}
          </div>
        );

      case 'portfolio':
        return (
          <div className="space-y-4">
            <button className="w-full bg-teal-600 text-white py-3 px-4 rounded-lg hover:bg-teal-700 flex items-center justify-center gap-2">
              <Upload className="w-5 h-5" />
              Ajouter un fichier
            </button>
            {data?.portfolio?.files?.map((file, i) => (
              <div key={i} className="border border-gray-200 rounded-lg p-4 flex items-center justify-between">
                <div>
                  <div className="font-medium text-gray-900">{file.name}</div>
                  <div className="text-sm text-gray-600">{file.category} • {file.uploadedAt}</div>
                </div>
                <span className={`px-3 py-1 text-xs rounded-full ${file.status === 'validated' ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}`}>
                  {file.status === 'validated' ? 'Validé' : 'En attente'}
                </span>
              </div>
            ))}
          </div>
        );

      case 'suggestions':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="font-semibold text-gray-900 mb-4">Mobilité horizontale</h3>
              {data?.suggestions?.horizontal?.map((sug, i) => (
                <div key={i} className="border border-gray-200 rounded-lg p-4 mb-3 hover:border-teal-300">
                  <div className="flex items-center justify-between mb-2">
                    <div className="font-medium text-gray-900">{sug.job}</div>
                    <div className="text-xl font-bold text-teal-600">{sug.matchScore}%</div>
                  </div>
                  <div className="text-sm text-gray-600">Formation requise: {sug.requiredTraining}</div>
                </div>
              ))}
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-4">Mobilité verticale</h3>
              {data?.suggestions?.vertical?.map((sug, i) => (
                <div key={i} className="border border-gray-200 rounded-lg p-4 mb-3 hover:border-purple-300">
                  <div className="flex items-center justify-between mb-2">
                    <div className="font-medium text-gray-900">{sug.job}</div>
                    <div className="text-xl font-bold text-purple-600">{sug.matchScore}%</div>
                  </div>
                  <div className="text-sm text-gray-600">Formation requise: {sug.requiredTraining}</div>
                </div>
              ))}
            </div>
          </div>
        );

      case 'eligibility':
        return (
          <div className="space-y-4">
            <div className={`border-l-4 p-6 rounded-lg ${data?.simulation?.eligible ? 'border-green-500 bg-green-50' : 'border-orange-500 bg-orange-50'}`}>
              <div className="text-xl font-bold mb-2">{data?.simulation?.eligible ? '✓ Vous êtes éligible à la VAE' : 'Conditions non remplies'}</div>
              <div className="text-sm text-gray-700">{data?.simulation?.recommendation}</div>
            </div>
            {data?.simulation?.criteria?.map((crit, i) => (
              <div key={i} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium">{crit.name}</div>
                    <div className="text-sm text-gray-600">Requis: {crit.required} • Actuel: {crit.actual}</div>
                  </div>
                  {crit.met ? <CheckCircle className="w-6 h-6 text-green-600" /> : <span className="w-6 h-6 text-orange-600">✗</span>}
                </div>
              </div>
            ))}
          </div>
        );

      case 'guide':
        return (
          <div className="space-y-6">
            <h2 className="text-xl font-bold text-gray-900">{data?.guide?.title}</h2>
            {data?.guide?.sections?.map((section, i) => (
              <div key={i} className="border border-gray-200 rounded-lg p-5">
                <h3 className="font-semibold text-gray-900 mb-3">{section.title}</h3>
                <div className="space-y-2 mb-4">
                  {section.questions.map((q, j) => (
                    <div key={j} className="text-sm text-gray-700">• {q}</div>
                  ))}
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <div className="text-xs font-semibold text-blue-900 mb-1">Conseils:</div>
                  {section.tips.map((tip, k) => (
                    <div key={k} className="text-xs text-blue-800">• {tip}</div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        );

      case 'export':
        return (
          <div className="space-y-4">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Partenaires VAE agréés</h2>
            {data?.partners?.map((partner, i) => (
              <div key={i} className="border border-gray-200 rounded-lg p-5 hover:border-teal-300">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <div className="font-semibold text-gray-900">{partner.name}</div>
                    <div className="text-sm text-gray-600">{partner.email}</div>
                  </div>
                  {partner.certified && <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">Certifié</span>}
                </div>
                <div className="text-sm text-gray-600">Spécialités: {partner.specialties.join(', ')}</div>
                <button className="mt-3 w-full bg-teal-600 text-white py-2 px-4 rounded-lg hover:bg-teal-700 text-sm">Transmettre mon dossier</button>
              </div>
            ))}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-500 flex items-center justify-center text-white shadow-lg">
              <Award className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-3xl font-heading font-bold text-gray-900">Écheveau</h1>
              <p className="text-gray-600">VAE & Mobilité - Transitions professionnelles</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-2 mb-6">
          <div className="flex flex-wrap gap-2">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-all ${activeTab === tab.id ? 'bg-gradient-to-r from-teal-500 to-cyan-500 text-white shadow-md' : 'text-gray-600 hover:bg-gray-100'}`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="hidden sm:inline">{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          {renderContent()}
        </div>
      </div>
    </div>
  );
}
